import { Plus, Trash2, Copy, Wand2 } from 'lucide-react';
import { formatDateDisplay } from '../../utils/date';
import {
  blocksFromValue,
  buildDailyBlockSlots,
  copyDayToDates,
  datesInRange,
  groupDraftsByDate,
  meetingModeLabel,
  minutesToTime,
  normalizeMeetingMode,
  slotsFromSchedule,
  timeToMinutes,
  toHHMM,
  SIGNUP_MEETING_MODES,
  type SignupMeetingMode,
  type ScheduleMode,
  type SignupScheduleValue,
  type SlotDraft,
} from '../../utils/signupSlots';

const MODE_SELECT_CLASS =
  'border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

function MeetingModeSelect({
  value,
  onChange,
  id,
}: {
  value: SignupMeetingMode | '';
  onChange: (next: SignupMeetingMode | '') => void;
  id?: string;
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as SignupMeetingMode | '')}
      className={MODE_SELECT_CLASS}
    >
      <option value="">Not specified</option>
      {SIGNUP_MEETING_MODES.map((mode) => (
        <option key={mode} value={mode}>
          {meetingModeLabel(mode)}
        </option>
      ))}
    </select>
  );
}

/**
 * Authoring UI for an event's slots.
 *
 * Two modes, because conference rounds come in two shapes. "Same times every day" is the
 * original behaviour — two blocks repeated across the range — now with a cadence (a 15-minute
 * meeting can start every 30 minutes) and a meeting mode per block. "Custom times per day" is
 * the escape hatch: an explicit list of slots per day, which is the only thing that can express
 * a round where one day drops its afternoon and the evening times are spaced by hand.
 *
 * Switching to custom seeds the list from the daily blocks, so the common case is still "fill
 * in two blocks, then fix the one day that differs" rather than typing forty rows.
 */
export function SignupScheduleEditor({
  value,
  onChange,
  idPrefix,
}: {
  value: SignupScheduleValue;
  onChange: (patch: Partial<SignupScheduleValue>) => void;
  idPrefix: string;
}) {
  const dates = datesInRange(value.startDate, value.endDate);
  const preview = slotsFromSchedule(value);
  const byDate = groupDraftsByDate(preview);
  const inRange = new Set(dates);
  const skippedRows = value.customSlots.filter((slot) => inRange.has(slot.date)).length - preview.length;

  const seedFromDailyBlocks = (): SlotDraft[] =>
    buildDailyBlockSlots({
      startDate: value.startDate,
      endDate: value.endDate,
      slotDurationMinutes: value.slotDurationMinutes,
      slotIntervalMinutes: value.slotIntervalMinutes,
      blocks: blocksFromValue(value),
    });

  const setScheduleMode = (mode: ScheduleMode) => {
    if (mode === value.scheduleMode) return;
    if (mode === 'custom' && value.customSlots.length === 0) {
      onChange({ scheduleMode: mode, customSlots: seedFromDailyBlocks() });
      return;
    }
    onChange({ scheduleMode: mode });
  };

  // Rows are stored exactly as authored — no sorting or de-duping here. A time input is
  // momentarily blank while it is being retyped, and normalising on every keystroke would
  // delete the row out from under the admin. Cleanup happens once, at build time.
  const updateCustomSlots = (next: SlotDraft[]) => {
    onChange({ customSlots: next });
  };

  const addSlot = (date: string) => {
    const existing = value.customSlots.filter((slot) => slot.date === date);
    // A new row lands one cadence step after the day's latest slot, which is almost always
    // where the admin was going to put it anyway.
    const latest = existing.reduce<SlotDraft | null>((best, slot) => {
      const minutes = timeToMinutes(slot.startTime);
      if (!Number.isFinite(minutes)) return best;
      return !best || minutes > timeToMinutes(best.startTime) ? slot : best;
    }, null);
    const startMinutes = latest
      ? timeToMinutes(latest.startTime) + (value.slotIntervalMinutes || value.slotDurationMinutes)
      : timeToMinutes(value.morningStartTime || '09:00');

    updateCustomSlots([
      ...value.customSlots,
      {
        date,
        startTime: minutesToTime(startMinutes),
        endTime: minutesToTime(startMinutes + value.slotDurationMinutes),
        meetingMode: latest?.meetingMode ?? normalizeMeetingMode(value.morningMeetingMode),
      },
    ]);
  };

  const updateSlot = (date: string, index: number, patch: Partial<SlotDraft>) => {
    const dayKeys = value.customSlots
      .map((slot, i) => ({ slot, i }))
      .filter(({ slot }) => slot.date === date);
    const target = dayKeys[index];
    if (!target) return;

    const next = [...value.customSlots];
    const updated = { ...next[target.i], ...patch };

    // Keep the end after the start: nudging a start time drags the end with it unless the
    // admin is editing the end itself.
    if (patch.startTime && !patch.endTime) {
      const startMinutes = timeToMinutes(updated.startTime);
      const endMinutes = timeToMinutes(updated.endTime);
      if (!Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
        updated.endTime = minutesToTime(startMinutes + value.slotDurationMinutes);
      }
    }

    next[target.i] = updated;
    updateCustomSlots(next);
  };

  const removeSlot = (date: string, index: number) => {
    const dayKeys = value.customSlots
      .map((slot, i) => ({ slot, i }))
      .filter(({ slot }) => slot.date === date);
    const target = dayKeys[index];
    if (!target) return;

    updateCustomSlots(value.customSlots.filter((_, i) => i !== target.i));
  };

  const copyDay = (date: string) => {
    const others = dates.filter((d) => d !== date);
    if (others.length === 0) return;
    if (
      !window.confirm(
        `Copy ${formatDateDisplay(date)}'s slots to the other ${others.length} day(s)? Their current slots will be replaced.`
      )
    ) {
      return;
    }
    updateCustomSlots(copyDayToDates(value.customSlots, date, dates));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <h4 className="text-sm font-medium text-gray-700">Schedule</h4>
        <div className="flex items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name={`${idPrefix}-schedule-mode`}
              checked={value.scheduleMode === 'daily'}
              onChange={() => setScheduleMode('daily')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            Same times every day
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name={`${idPrefix}-schedule-mode`}
              checked={value.scheduleMode === 'custom'}
              onChange={() => setScheduleMode('custom')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            Custom times per day
          </label>
        </div>
      </div>

      {value.scheduleMode === 'daily' ? (
        <div className="space-y-4">
          <div>
            <label htmlFor={`${idPrefix}-interval`} className="block text-xs text-gray-600 mb-1">
              Start a slot every
            </label>
            <select
              id={`${idPrefix}-interval`}
              value={value.slotIntervalMinutes}
              onChange={(e) => onChange({ slotIntervalMinutes: parseInt(e.target.value, 10) })}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[15, 20, 30, 45, 60].map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} minutes
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Leave this at the slot length for back-to-back meetings, or set it wider to leave a gap
              between them (15-minute meetings every 30 minutes).
            </p>
          </div>

          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-700">Morning Time Slots</h5>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                <input
                  type="time"
                  value={value.morningStartTime}
                  onChange={(e) => onChange({ morningStartTime: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Time</label>
                <input
                  type="time"
                  value={value.morningEndTime}
                  onChange={(e) => onChange({ morningEndTime: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Held</label>
                <MeetingModeSelect
                  value={value.morningMeetingMode}
                  onChange={(mode) => onChange({ morningMeetingMode: mode })}
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-700">Evening Time Slots</h5>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                <input
                  type="time"
                  value={value.eveningStartTime}
                  onChange={(e) => onChange({ eveningStartTime: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Time</label>
                <input
                  type="time"
                  value={value.eveningEndTime}
                  onChange={(e) => onChange({ eveningEndTime: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Held</label>
                <MeetingModeSelect
                  value={value.eveningMeetingMode}
                  onChange={(mode) => onChange({ eveningMeetingMode: mode })}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateCustomSlots(seedFromDailyBlocks())}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              title="Replace every day's slots with the morning/evening blocks above"
            >
              <Wand2 className="h-4 w-4 mr-1" />
              Fill from daily blocks
            </button>
            <span className="text-xs text-gray-500">
              Then adjust the days that differ. Each slot can be held in person or on Google Meet.
            </span>
          </div>

          {dates.length === 0 ? (
            <p className="text-sm text-gray-500">Pick a start and end date to add slots.</p>
          ) : (
            <div className="space-y-4">
              {dates.map((date) => {
                const daySlots = value.customSlots.filter((slot) => slot.date === date);

                return (
                  <div key={date} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <h5 className="text-sm font-medium text-gray-800">
                        {formatDateDisplay(date)}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {daySlots.length} slot{daySlots.length === 1 ? '' : 's'}
                        </span>
                      </h5>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => addSlot(date)}
                          className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-xs text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add slot
                        </button>
                        <button
                          type="button"
                          onClick={() => copyDay(date)}
                          disabled={dates.length < 2 || daySlots.length === 0}
                          className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-xs text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          title="Copy this day's slots to every other day"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copy to all days
                        </button>
                      </div>
                    </div>

                    {daySlots.length === 0 ? (
                      <p className="text-xs text-gray-500">No slots on this day.</p>
                    ) : (
                      <div className="space-y-2">
                        {daySlots.map((slot, index) => (
                          <div key={`${date}-${index}`} className="flex flex-wrap items-center gap-2">
                            <input
                              type="time"
                              aria-label={`Start time for slot ${index + 1} on ${formatDateDisplay(date)}`}
                              value={toHHMM(slot.startTime)}
                              onChange={(e) => updateSlot(date, index, { startTime: e.target.value })}
                              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-gray-400 text-sm">–</span>
                            <input
                              type="time"
                              aria-label={`End time for slot ${index + 1} on ${formatDateDisplay(date)}`}
                              value={toHHMM(slot.endTime)}
                              onChange={(e) => updateSlot(date, index, { endTime: e.target.value })}
                              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <MeetingModeSelect
                              value={slot.meetingMode ?? ''}
                              onChange={(mode) =>
                                updateSlot(date, index, { meetingMode: normalizeMeetingMode(mode) })
                              }
                            />
                            <button
                              type="button"
                              onClick={() => removeSlot(date, index)}
                              className="inline-flex items-center px-2 py-1 text-xs text-red-600 hover:text-red-800"
                              aria-label={`Remove slot ${index + 1} on ${formatDateDisplay(date)}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {value.scheduleMode === 'custom' && skippedRows > 0 && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
          {skippedRows} row{skippedRows === 1 ? '' : 's'} will be skipped: a row is only saved once it
          has a start and end time, ends after it starts, and no other slot that day starts at the
          same time.
        </p>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700">
        <strong>{preview.length}</strong> slot{preview.length === 1 ? '' : 's'} across{' '}
        <strong>{byDate.length}</strong> day{byDate.length === 1 ? '' : 's'}
        {preview.length > 0 && (
          <span className="text-gray-500">
            {' '}
            · {preview.filter((s) => s.meetingMode === 'google_meet').length} Google Meet ·{' '}
            {preview.filter((s) => s.meetingMode === 'in_person').length} in person
          </span>
        )}
      </div>
    </div>
  );
}
